import { CommonModule } from '@angular/common';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  AxsButtonComponent,
  AxsIconComponent,
  AxsInputTextComponent,
  AxsModalComponent,
} from '@vanguard/axs-angular';
import { ContentService } from 'src/app/shared/services/content/content.service';
import { content } from 'src/assets/content/fallback-content';

import { AdobeLaunchService } from '../../adobe-launch/adobe-launch.service';
import { PiiMaskDirective } from '../../shared/util/pii-mask.directive';

@Component({
  selector: 'client-ready-report-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    AxsButtonComponent,
    AxsIconComponent,
    AxsInputTextComponent,
    AxsModalComponent,
    PiiMaskDirective,
  ],
  templateUrl: './client-ready-report-modal.component.html',
  styleUrl: './client-ready-report-modal.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ClientReadyReportModalComponent implements OnChanges {
  @Input() isOpen = false;

  @Output() isOpenChange = new EventEmitter<boolean>();
  @Output() downloadWithCustomization = new EventEmitter<string>();
  @Output() downloadWithoutCustomization = new EventEmitter<void>();

  readonly clientNameMaxLength = 50;

  clientName = '';
  shouldRenderInput = false;
  clientReadyReportModalContent = content.Data.v4_rcc_root.content.clientReadyReportModal;
  private suppressCloseTagOnNextClose = false;

  constructor(
    private readonly adobeLaunchService: AdobeLaunchService,
    private readonly contentService: ContentService,
  ) {
    this.contentService.getContent().subscribe((data) => {
      this.clientReadyReportModalContent =
        data.Data.v4_rcc_root.content.clientReadyReportModal ?? this.clientReadyReportModalContent;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['isOpen']) {
      return;
    }

    if (changes['isOpen'].currentValue !== changes['isOpen'].previousValue) {
      this.resetClientName();
      this.shouldRenderInput = changes['isOpen'].currentValue;
    }
  }

  get hasClientName(): boolean {
    return this.clientName.trim().length > 0;
  }

  get remainingCharacters(): number {
    return this.clientNameMaxLength - this.clientName.length;
  }

  get remainingCharactersText(): string {
    if (this.clientName.length === 0) {
      return this.clientReadyReportModalContent?.youCanEnterUpTo50Char || '';
    }

    const remainingCharactersLabel = this.clientReadyReportModalContent?.youHave || '';
    const remainingCharactersSuffix =
      this.remainingCharacters === 1
        ? this.clientReadyReportModalContent?.characterRemaining || ''
        : this.clientReadyReportModalContent?.charactersRemaining || '';
    return `${remainingCharactersLabel} ${this.remainingCharacters} ${remainingCharactersSuffix}`;
  }

  onModalClosed(): void {
    if (!this.suppressCloseTagOnNextClose) {
      this.adobeLaunchService.captureCtaButton('chart:download-client-ready-report-close');
    }
    this.suppressCloseTagOnNextClose = false;
    this.resetClientName();
    this.shouldRenderInput = false;
    this.isOpenChange.emit(false);
  }

  onClientNameInput(event: unknown): void {
    this.clientName = this.extractInputValue(event).slice(0, this.clientNameMaxLength);
  }

  onDownloadWithCustomization(): void {
    if (!this.hasClientName) {
      return;
    }

    const clientName = this.clientName.trim();
    this.suppressCloseTagOnNextClose = true;
    this.downloadWithCustomization.emit(clientName);
  }

  onDownloadWithoutCustomization(): void {
    if (this.hasClientName) {
      return;
    }

    this.suppressCloseTagOnNextClose = true;
    this.downloadWithoutCustomization.emit();
  }

  private extractInputValue(event: unknown): string {
    if (typeof event === 'string') {
      return event;
    }

    if (!event || typeof event !== 'object') {
      return '';
    }

    const eventValue = this.readStringValue(event, 'value');
    if (eventValue) {
      return eventValue;
    }

    const detailValue = this.readNestedStringValue(event, 'detail', 'value');
    if (detailValue) {
      return detailValue;
    }

    const targetValue = this.readNestedStringValue(event, 'target', 'value');
    if (targetValue) {
      return targetValue;
    }

    return this.readNestedStringValue(event, 'currentTarget', 'value');
  }

  private readStringValue(source: object, key: string): string {
    const value = (source as Record<string, unknown>)[key];
    return typeof value === 'string' ? value : '';
  }

  private readNestedStringValue(source: object, parentKey: string, childKey: string): string {
    const parentValue = (source as Record<string, unknown>)[parentKey];

    if (!parentValue || typeof parentValue !== 'object') {
      return '';
    }

    return this.readStringValue(parentValue, childKey);
  }

  private resetClientName(): void {
    this.clientName = '';
  }
}
